import React, { useEffect, useState } from 'react';
import { Clock, LogIn, LogOut, MapPin, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { getLocation, fmtTime, setAgencyTz } from '../../utils/timeclock';
import { StatusChip, GeoFlag } from './VisitBits';

const Elapsed = ({ since }) => {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(t); }, []);
  const m = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 60000));
  return <span>{Math.floor(m / 60)}h {m % 60}m</span>;
};

const OpenBox = ({ entry, label, testid, onOut, busy, children }) => (
  <div className="rounded-lg border border-green-300 bg-green-50 p-3" data-testid={`${testid}-open`}>
    <p className="text-sm text-green-900"><strong>{label}</strong> since {fmtTime(entry.clockIn)} · <Elapsed since={entry.clockIn} />
      {entry.inLocation?.lat == null && <span className="text-amber-700"> · no location</span>}</p>
    {entry.kind === 'visit' && <p className="text-xs mt-1"><GeoFlag geo={entry.inGeo} /></p>}
    {children}
    <button disabled={busy} onClick={onOut} data-testid={`${testid}-clock-out`} className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
      <LogOut className="w-4 h-4" /> Clock out
    </button>
  </div>
);

export const TimeClockCard = ({ onChange }) => {
  const [st, setSt] = useState(null);
  const [patients, setPatients] = useState([]);
  const [pid, setPid] = useState('');
  const [vtype, setVtype] = useState('Routine Visit');
  const [miles, setMiles] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [today, setToday] = useState([]);
  const [apptId, setApptId] = useState(null);
  const load = () => Promise.all([
    api.get('/timeclock/status').then((r) => { setAgencyTz(r.data.timezone); setSt(r.data); }),
    api.get('/timeclock/my-visits').then((r) => setToday(r.data.visits)),
  ]).catch(() => {});
  useEffect(() => { load(); api.get('/patients').then((r) => setPatients(r.data.patients || [])).catch(() => {}); }, []);

  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      const location = await getLocation();
      if (location.error) toast.warning(`${location.error}. The time was saved without a location.`);
      await api.post(path, { ...body, location });
      toast.success(msg);
      setMiles(''); setNotes(''); setApptId(null);
      await load();
      if (path.endsWith('clock-in') && body.kind === 'visit') setTimeout(load, 5000);
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the time'); }
    setBusy(false);
  };
  if (!st) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4" data-testid="time-clock-card">
      <h3 className="font-semibold text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-0.5"><Clock className="w-5 h-5 text-[#b8860b]" /> Time Clock
        <span className="text-xs font-normal text-gray-500 flex items-center gap-1 basis-full sm:basis-auto"><MapPin className="w-3 h-3" /> location is recorded at each clock in and out</span></h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-700">Shift</p>
          {st.shift
            ? <OpenBox entry={st.shift} label="On shift" testid="shift" busy={busy} onOut={() => act('/timeclock/clock-out', { entryId: st.shift.id }, 'Clocked out of your shift')} />
            : <button disabled={busy} onClick={() => act('/timeclock/clock-in', { kind: 'shift' }, 'Clocked in for your shift')} data-testid="shift-clock-in"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-gray-900 text-[#d4af37] font-semibold hover:bg-black disabled:opacity-50"><LogIn className="w-4 h-4" /> Clock in for shift</button>}
        </div>
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-700 flex items-center gap-1"><Stethoscope className="w-4 h-4" /> Patient visit</p>
          {st.visit ? (
            <OpenBox entry={st.visit} label={`${st.visit.visitType} – ${st.visit.patientName}`} testid="visit" busy={busy}
              onOut={() => act('/timeclock/clock-out', { entryId: st.visit.id, mileage: miles === '' ? null : Number(miles), notes }, 'Visit clocked out')}>
              <div className="flex flex-wrap gap-2 mt-2">
                <input type="number" min="0" step="0.1" value={miles} onChange={(e) => setMiles(e.target.value)} placeholder="Miles driven" className="w-28 border rounded px-2 py-1 text-sm" data-testid="visit-mileage" />
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (optional)" className="flex-1 min-w-[8rem] border rounded px-2 py-1 text-sm" data-testid="visit-notes" />
              </div>
            </OpenBox>
          ) : (
            <div className="flex flex-wrap gap-2">
              <select value={pid} onChange={(e) => { setPid(e.target.value); setApptId(null); }} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-[10rem]" data-testid="visit-patient-select">
                <option value="">Choose patient…</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.lastName}, {p.firstName}{p.medicalRecordNumber ? ` (MRN ${p.medicalRecordNumber})` : ''}</option>)}
              </select>
              <select value={vtype} onChange={(e) => setVtype(e.target.value)} className="border rounded-lg px-2 py-2 text-sm" data-testid="visit-type-select">
                {st.visitTypes.map((v) => <option key={v}>{v}</option>)}
              </select>
              <button disabled={busy || !pid} onClick={() => act('/timeclock/clock-in', { kind: 'visit', patientId: pid, visitType: vtype, appointmentId: apptId }, 'Visit clocked in')} data-testid="visit-clock-in"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d4af37] text-gray-900 font-semibold hover:bg-[#c9a52f] disabled:opacity-50"><LogIn className="w-4 h-4" /> Start visit</button>
            </div>
          )}
        </div>
      </div>
      {today.length > 0 && (
        <div data-testid="my-visits-today">
          <p className="text-sm font-semibold text-gray-700 mb-1">Today's scheduled visits</p>
          <ul className="divide-y border rounded-lg">
            {today.map((v) => (
              <li key={v.appointmentId} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`my-visit-${v.appointmentId}`}>
                <span className="font-semibold w-20">{v.time}</span><span className="flex-1 min-w-[8rem]">{v.patientName} · {v.type}</span>
                <StatusChip status={v.status} />
                {!st.visit && !v.entry && v.status !== 'Cancelled' && (
                  <button onClick={() => { setPid(v.patientId); setApptId(v.appointmentId); if (st.visitTypes.includes(v.type)) setVtype(v.type); }}
                    className="text-xs px-2 py-1 rounded border border-[#d4af37] hover:bg-amber-50" data-testid={`my-visit-pick-${v.appointmentId}`}>Choose</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
